/**
 * Time-slot draft helpers shared by the assign/escalate drawer and the ticket
 * header's "Assign to me" popover, so both seed and edit rows identically.
 */

import { dayjs } from "utilz";
import { v4 as uuidv4 } from "uuid";
import type { TimeSlotDraft } from "../types";

// "Now" in the RESOURCE timezone, rounded UP to the next whole minute (not
// floored) so it is never in the past — flooring seconds could leave it up to
// ~59s behind, which a strict backend might reject on an immediate save.
export const nextWholeMinute = (timezone: string) => {
  const now = dayjs().tz(timezone);
  const floored = now.second(0).millisecond(0);
  return floored.isBefore(now) ? floored.add(1, "minute") : floored;
};

// Slot seeded by the header-level AddScheduleIcon when a member has no schedule
// yet. Pre-fills Start with "now" and End with now + 1h, computed in the
// RESOURCE timezone (not system time) so the picker opens on the resource's
// current wall-clock instead of empty "Start date & time" placeholders.
// The copy affordance has its own source-slot path and does not use this.
export const buildNowTimeSlot = (timezone: string): TimeSlotDraft => {
  const start = nextWholeMinute(timezone);
  return {
    scheduleId: null,
    slotUuid: uuidv4(),
    startIso: start.toISOString(),
    endIso: start.add(1, "hour").toISOString(),
    error: { start: null, end: null },
  };
};

// Times are change-only: a slot always has both Start and End once it exists
// (seeded on add, derived here). A patch that would null/empty either field
// is rejected (returns null) — removal is delete-the-whole-row, never
// clear-a-field. A new Start with no End — or an End not after it — pushes End
// to Start + 1h; otherwise the user's End is preserved.
export const applySlotPatch = (
  slot: TimeSlotDraft,
  patch: Partial<Pick<TimeSlotDraft, "startIso" | "endIso">>,
): TimeSlotDraft | null => {
  if (
    ("startIso" in patch && !patch.startIso) ||
    ("endIso" in patch && !patch.endIso)
  ) {
    return null;
  }
  const merged = { ...slot, ...patch };
  if (patch.startIso) {
    const noEnd = merged.endIso === null;
    const endNotAfterStart =
      !noEnd && dayjs(merged.endIso).diff(dayjs(patch.startIso)) <= 0;
    if (noEnd || endNotAfterStart) {
      merged.endIso = dayjs(patch.startIso).add(1, "hour").toISOString();
    }
  }
  return merged;
};
